import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { RASCUNHO_VAZIO, type AnexoDoRascunho, type Rascunho } from './rascunho';

/**
 * Rascunhos no servidor (ADR 0052): o formato que viaja e a conversao de e para
 * o `Rascunho` do composer. Sem React e sem estado, para dar para testar.
 */

export interface ResumoDeRascunho {
  id: string;
  accountId: string;
  subject: string;
  snippet: string;
  version: number;
  updatedAt: string;
  temAnexos: boolean;
}

export interface RascunhoDoServidor {
  id: string;
  accountId: string;
  subject: string;
  version: number;
  updatedAt: string;
  identidade: string;
  to: { name: string; address: string }[];
  cc: { name: string; address: string }[];
  bcc: { name: string; address: string }[];
  formato: 'html' | 'texto';
  html: string;
  texto: string;
  citando: boolean;
  confirmacaoDeLeitura: boolean;
  agendadoPara: string | null;
  emRespostaA: string | null;
  referencias: string[];
  anexosPorReferencia: {
    id: string;
    filename: string;
    sizeBytes: number;
    contentType: string;
    origem: { messageId: string; anexoId: string };
  }[];
  anexosEnviados: { id: string; filename: string; contentType: string; sizeBytes: number }[];
}

export const chavesDeRascunho = {
  lista: ['rascunhos'] as const,
  um: (id: string) => ['rascunhos', id] as const,
};

/** O corpo do `PUT /drafts/:id`. */
export function paraGravacao(
  rascunho: Rascunho,
  accountId: string,
  baseVersion: number | null,
  token: string,
) {
  const { anexos, ...resto } = rascunho;
  return {
    ...resto,
    accountId,
    baseVersion,
    token,
    // So a referencia a anexo de outra mensagem vai no corpo; arquivo vai por
    // `POST /drafts/:id/attachments`, uma vez, a parte.
    anexosPorReferencia: anexos.flatMap((a) =>
      a.origem === undefined
        ? []
        : [
            {
              id: a.id,
              filename: a.filename,
              sizeBytes: a.sizeBytes,
              contentType: a.contentType,
              origem: a.origem,
            },
          ],
    ),
  };
}

export function doServidor(d: RascunhoDoServidor): Rascunho {
  const referencias: AnexoDoRascunho[] = d.anexosPorReferencia.map((a) => ({
    id: a.id,
    filename: a.filename,
    sizeBytes: a.sizeBytes,
    contentType: a.contentType,
    progresso: 100,
    origem: a.origem,
  }));
  const enviados: AnexoDoRascunho[] = d.anexosEnviados.map((a) => ({
    ...a,
    progresso: 100,
    servidor: true,
  }));

  return {
    ...RASCUNHO_VAZIO,
    identidade: d.identidade,
    to: d.to,
    cc: d.cc,
    bcc: d.bcc,
    subject: d.subject,
    formato: d.formato,
    html: d.html,
    texto: d.texto,
    citando: d.citando,
    confirmacaoDeLeitura: d.confirmacaoDeLeitura,
    agendadoPara: d.agendadoPara,
    emRespostaA: d.emRespostaA,
    referencias: d.referencias,
    anexos: [...referencias, ...enviados],
  };
}

export function gravarNoServidor(
  id: string,
  rascunho: Rascunho,
  accountId: string,
  baseVersion: number | null,
  token: string,
): Promise<{ version: number; updatedAt: string }> {
  return api(`/drafts/${id}`, {
    method: 'PUT',
    body: paraGravacao(rascunho, accountId, baseVersion, token),
  });
}

export function lerDoServidor(id: string): Promise<RascunhoDoServidor> {
  return api<RascunhoDoServidor>(`/drafts/${id}`);
}

export function apagarNoServidor(id: string): Promise<void> {
  return api<void>(`/drafts/${id}`, { method: 'DELETE' });
}

export function anexarAoRascunho(
  id: string,
  arquivo: { filename: string; contentType: string; content: string },
) {
  return api<{ id: string; filename: string; contentType: string; sizeBytes: number }>(
    `/drafts/${id}/attachments`,
    { method: 'POST', body: arquivo },
  );
}

export function removerAnexoDoRascunho(id: string, anexoId: string): Promise<void> {
  return api<void>(`/drafts/${id}/attachments/${anexoId}`, { method: 'DELETE' });
}

/** Envio agendado que ainda espera a hora (ADR 0066). */
export interface Agendada {
  id: string;
  accountId: string;
  para: string[];
  assunto: string;
  scheduledAt: string;
}

/**
 * Cancela um envio que ainda nao saiu. A mensagem volta para Rascunhos com um
 * id novo (`null` so se a fila nao tinha o MIME); 409 quando ja saiu.
 */
export function cancelarEnvio(id: string): Promise<{ rascunhoId: string | null }> {
  return api(`/outbound/${id}`, { method: 'DELETE' });
}

export function mudarHorario(id: string, sendAt: Date): Promise<{ scheduledAt: string }> {
  return api(`/outbound/${id}`, { method: 'PATCH', body: { sendAt: sendAt.toISOString() } });
}

/** Sob a chave dos rascunhos: tudo que atualiza a lista de Rascunhos atualiza esta. */
export function useAgendadas(accountId: string | undefined) {
  return useQuery({
    queryKey: [...chavesDeRascunho.lista, 'agendadas', accountId ?? null],
    queryFn: () =>
      api<Agendada[]>(
        accountId === undefined
          ? '/outbound/scheduled'
          : `/outbound/scheduled?accountId=${accountId}`,
      ),
    staleTime: 0,
    refetchInterval: 30_000,
  });
}

/** Lista da pasta Rascunhos. Atualiza sozinha: um rascunho de outro aparelho aparece aqui. */
export function useRascunhos(accountId: string | undefined, ativo: boolean) {
  return useQuery({
    queryKey: [...chavesDeRascunho.lista, accountId ?? null],
    queryFn: () =>
      api<ResumoDeRascunho[]>(
        accountId === undefined ? '/drafts' : `/drafts?accountId=${accountId}`,
      ),
    enabled: ativo,
    staleTime: 0,
    refetchInterval: 15_000,
  });
}
